import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { SERIES_COLORS } from '../utils/auditHelpers';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const cleanDate = dateStr.split('T')[0];
    const parts = cleanDate.split('-');
    if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
    }
    return dateStr;
};

const EquityChartContainer = ({
    chartView,
    cumulativeSubMode,
    multiGridCount,
    multiGridChartData,
    multiPanels,
    setMultiPanels,
    equityChartData,
    pnlUnit,
    selectedSeries,
    pinnedSeries,
    setPinnedSeries,
    hoveredSeries,
    setHoveredSeries,
    checkIsLiveGrid,
    sortedSeriesList,
    highlightLive,
    showWholeBaseline,
    showLiveGridFiltered
}) => {
    return (
        <div className="flex-1 min-h-0 w-full relative">
            {chartView === 'cumulative' && cumulativeSubMode === 'multi' ? (
                <div className={`grid gap-3 w-full h-full min-h-0 overflow-y-auto p-1 ${
                    multiGridCount === 2 ? 'grid-cols-1 md:grid-cols-2' :
                    multiGridCount === 4 ? 'grid-cols-1 md:grid-cols-2' :
                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                }`}>
                    {multiGridChartData.map((pData, idx) => {
                        const panelConfig = multiPanels[idx] || {};
                        const pFSR = panelConfig.selectedFSR || ['R', 'S', 'F'];
                        const pMABits = panelConfig.selectedMABits || ['00', '01', '10', '11'];
                        const pCEPE = panelConfig.selectedCEPE || ['CE', 'PE'];

                        return (
                            <div key={idx} className="bg-[#14161f] border border-gray-800 rounded-xl p-3 flex flex-col h-[290px] min-h-0 relative shadow-md">
                                {/* Header */}
                                <div className="flex items-center justify-between gap-2 border-b border-gray-800 pb-2 mb-2 shrink-0">
                                    <div className="flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[10px] font-mono font-extrabold border border-purple-500/30">
                                            #{idx + 1}
                                        </span>
                                        <select
                                            value={panelConfig.strategy || pData.strategy || 'ALL'}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, strategy: val } : p));
                                            }}
                                            className="bg-[#1a1d29] border border-gray-700 rounded px-2 py-0.5 text-xs font-bold text-gray-200 focus:outline-none max-w-[170px] truncate cursor-pointer"
                                        >
                                            <option value="ALL">🌐 All Strategies Portfolio</option>
                                            {(equityChartData.seriesList || []).map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex items-center gap-2 text-[10px] font-mono font-bold">
                                        <span className="text-gray-300" title="Whole Baseline">
                                            Base: {pnlUnit === 'inr' ? '₹' + Math.round(pData.wholeEndEquity || 0).toLocaleString('en-IN') : (pData.wholeEndEquity >= 0 ? '+' : '') + (pData.wholeEndEquity || 0).toFixed(1) + '%'}
                                        </span>
                                        <span className="text-emerald-400" title="Live Grid Filtered">
                                            Live: {pnlUnit === 'inr' ? '₹' + Math.round(pData.filteredEndEquity || 0).toLocaleString('en-IN') : (pData.filteredEndEquity >= 0 ? '+' : '') + (pData.filteredEndEquity || 0).toFixed(1) + '%'}
                                        </span>
                                    </div>
                                </div>

                                {/* Filter Toolbar */}
                                <div className="flex flex-wrap items-center justify-between gap-1 mb-2 text-[9px] shrink-0">
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 font-bold">FSR:</span>
                                        {['R', 'S', 'F'].map(k => {
                                            const isSel = pFSR.includes(k);
                                            return (
                                                <button
                                                    key={k}
                                                    onClick={() => {
                                                        const next = isSel ? pFSR.filter(x => x !== k) : [...pFSR, k];
                                                        setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, selectedFSR: next } : p));
                                                    }}
                                                    className={`px-1.5 py-0.2 rounded font-bold border transition-all ${isSel ? 'bg-gray-800 text-amber-400 border-gray-600' : 'bg-transparent text-gray-600 border-gray-800'}`}
                                                >
                                                    {k}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 font-bold">MA:</span>
                                        {['00', '01', '10', '11'].map(bit => {
                                            const isSel = pMABits.includes(bit);
                                            return (
                                                <button
                                                    key={bit}
                                                    onClick={() => {
                                                        const next = isSel ? pMABits.filter(x => x !== bit) : [...pMABits, bit];
                                                        setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, selectedMABits: next } : p));
                                                    }}
                                                    className={`px-1 py-0.2 rounded font-mono font-bold border transition-all ${isSel ? 'bg-blue-900/40 text-blue-300 border-blue-600' : 'bg-transparent text-gray-600 border-gray-800'}`}
                                                >
                                                    {bit}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 font-bold">OPT:</span>
                                        {['CE', 'PE'].map(t => {
                                            const isSel = pCEPE.includes(t);
                                            return (
                                                <button
                                                    key={t}
                                                    onClick={() => {
                                                        const next = isSel ? pCEPE.filter(x => x !== t) : [...pCEPE, t];
                                                        setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, selectedCEPE: next } : p));
                                                    }}
                                                    className={`px-1 py-0.2 rounded font-bold border transition-all ${isSel ? 'bg-purple-900/40 text-purple-300 border-purple-600' : 'bg-transparent text-gray-600 border-gray-800'}`}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Chart Container */}
                                <div className="flex-1 min-h-0 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={pData.data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" opacity={0.2} vertical={false} />
                                            <XAxis dataKey="date" stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={9} tickLine={false} tickFormatter={formatDate} />
                                            <YAxis stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={9} tickLine={false} tickFormatter={(v) => pnlUnit === 'inr' ? `₹${(v/1000).toFixed(0)}k` : `${v.toFixed(0)}%`} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', borderRadius: '0.5rem', fontSize: '10px' }}
                                                itemStyle={{ color: '#f3f4f6' }}
                                                labelFormatter={formatDate}
                                            />
                                            <Line type="monotone" dataKey="Whole Baseline" stroke="#FFFFFF" dot={false} strokeWidth={1.8} name="Whole Baseline" />
                                            <Line type="monotone" dataKey="Live Grid Filtered" stroke="#10B981" dot={false} strokeWidth={2.0} name="Live Grid Filtered" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : chartView === 'cumulative' && cumulativeSubMode === 'split' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full w-full">
                    {/* Chart 1: Whole Tradelist Baseline */}
                    <div className="bg-[#14161f] border border-gray-800 rounded-lg p-2 flex flex-col h-full">
                        <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-gray-800">
                            <span className="text-xs font-bold text-sky-400">Whole Tradelist Baseline</span>
                            <span className="text-[10px] text-gray-500 font-mono">Unfiltered Portfolio</span>
                        </div>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={equityChartData.data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" opacity={0.15} />
                                    <XAxis dataKey="date" stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={9} tickFormatter={formatDate} />
                                    <YAxis stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={9} tickFormatter={(v) => pnlUnit === 'inr' ? '₹' + v.toLocaleString('en-IN') : v.toFixed(1) + '%'} />
                                    <Tooltip labelFormatter={formatDate} />
                                    <Line type="monotone" dataKey="Whole Tradelist" stroke="#FFFFFF" dot={false} strokeWidth={2.0} name="Whole Tradelist" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart 2: Live Grid Filtered */}
                    <div className="bg-[#14161f] border border-emerald-500/30 rounded-lg p-2 flex flex-col h-full bg-emerald-500/5">
                        <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-gray-800">
                            <span className="text-xs font-bold text-emerald-400">Live Grid Filtered Selection</span>
                            <span className="text-[10px] text-emerald-500/80 font-mono">Regime Grid Active</span>
                        </div>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={equityChartData.data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" opacity={0.15} />
                                    <XAxis dataKey="date" stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={9} tickFormatter={formatDate} />
                                    <YAxis stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={9} tickFormatter={(v) => pnlUnit === 'inr' ? '₹' + v.toLocaleString('en-IN') : v.toFixed(1) + '%'} />
                                    <Tooltip labelFormatter={formatDate} />
                                    <Line type="monotone" dataKey="Live Grid Filtered" stroke="#10B981" dot={false} strokeWidth={3} name="Live Grid Filtered" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            ) : Object.values(selectedSeries).filter(Boolean).length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-xs">
                    Please select one or more series from the right sidebar to display.
                </div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={equityChartData.data}
                        margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" opacity={0.15} />
                        <XAxis
                            dataKey="date"
                            stroke="#4B5563"
                            tick={{ fill: '#9CA3AF' }}
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={formatDate}
                        />
                        <YAxis
                            stroke="#4B5563"
                            tick={{ fill: '#9CA3AF' }}
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => pnlUnit === 'inr' 
                                ? '₹' + v.toLocaleString('en-IN', { maximumFractionDigits: 0 })
                                : v.toFixed(1) + '%'
                            }
                        />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (!active || !payload || payload.length === 0) return null;

                                let items = payload;
                                if (chartView === 'daily') {
                                    items = payload.filter(item => Math.abs(Number(item.value) || 0) >= 0.001);
                                }

                                items = [...items].sort((a, b) => Math.abs(Number(b.value) || 0) - Math.abs(Number(a.value) || 0));

                                if (items.length === 0) return null;

                                const activeTargetSeries = pinnedSeries || hoveredSeries;

                                return (
                                    <div className="bg-[#14161f]/95 border border-gray-700/80 rounded-xl p-3 shadow-2xl backdrop-blur-md text-xs max-h-80 overflow-y-auto custom-scrollbar min-w-[260px] max-w-[360px] z-50">
                                        <div className="font-bold text-blue-400 border-b border-gray-800 pb-1.5 mb-2 flex items-center justify-between">
                                            <span>📅 {formatDate(label)}</span>
                                            <span className="text-[10px] text-gray-400 font-mono font-normal">({items.length} series)</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            {items.map((item) => {
                                                const seriesName = item.name || item.dataKey;
                                                const val = Number(item.value) || 0;
                                                const isTarget = activeTargetSeries === seriesName;
                                                const isPinned = pinnedSeries === seriesName;
                                                const isLive = checkIsLiveGrid(seriesName);

                                                return (
                                                    <div
                                                        key={seriesName}
                                                        className={`flex items-center justify-between gap-2 py-1 px-2 rounded transition-colors ${
                                                            isPinned
                                                                ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300 font-bold'
                                                                : isTarget
                                                                    ? 'bg-blue-600/20 border border-blue-500/40 text-yellow-300 font-semibold'
                                                                    : 'hover:bg-gray-800/40 text-gray-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                            <span
                                                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                                                style={{ backgroundColor: item.color || item.stroke }}
                                                            />
                                                            <span className="truncate font-semibold text-[11px] text-gray-200" title={seriesName}>
                                                                {isPinned ? `📌 ${seriesName}` : isTarget ? `► ${seriesName}` : seriesName}
                                                            </span>
                                                            {isLive && (
                                                                <span className="px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-400 text-[8px] font-extrabold uppercase shrink-0">
                                                                    LIVE
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className={`font-mono font-bold shrink-0 text-[11px] ${val >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                            {pnlUnit === 'inr'
                                                                ? (val >= 0 ? '+' : '') + '₹' + Math.round(val).toLocaleString('en-IN')
                                                                : (val >= 0 ? '+' : '') + val.toFixed(2) + '%'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        {/* 1. Strategy Series Lines */}
                        {sortedSeriesList.map((seriesName) => {
                            if (!selectedSeries[seriesName]) return null;
                            const originalIndex = equityChartData.seriesList.indexOf(seriesName);
                            const color = SERIES_COLORS[originalIndex % SERIES_COLORS.length];
                            const isLive = checkIsLiveGrid(seriesName);
                            const activeTargetSeries = pinnedSeries || hoveredSeries;
                            const isTarget = activeTargetSeries === seriesName;
                            const isAnyTarget = Boolean(activeTargetSeries);
                            const isPinned = pinnedSeries === seriesName;

                            let strokeWidth = 1.5;
                            let strokeOpacity = 1.0;
                            let strokeDasharray = undefined;

                            if (isAnyTarget) {
                                if (isTarget) {
                                    strokeWidth = isPinned ? 2.8 : 2.4;
                                    strokeOpacity = 1.0;
                                } else {
                                    strokeWidth = 1.0;
                                    strokeOpacity = 0.08;
                                }
                            } else if (highlightLive) {
                                strokeWidth = isLive ? 2.2 : 1.0;
                                strokeOpacity = isLive ? 1.0 : 0.25;
                                strokeDasharray = isLive ? undefined : '4 4';
                            }

                            return (
                                <Line
                                    key={seriesName}
                                    type="monotone"
                                    dataKey={seriesName}
                                    stroke={color}
                                    dot={false}
                                    strokeWidth={strokeWidth}
                                    strokeOpacity={strokeOpacity}
                                    strokeDasharray={strokeDasharray}
                                    activeDot={{ r: isTarget ? (isPinned ? 8 : 7) : (highlightLive && isLive ? 6 : 4), strokeWidth: isTarget ? 2 : 0, stroke: '#ffffff' }}
                                    name={seriesName}
                                    onMouseEnter={() => setHoveredSeries(seriesName)}
                                    onMouseLeave={() => setHoveredSeries(null)}
                                    onClick={() => setPinnedSeries(prev => prev === seriesName ? null : seriesName)}
                                />
                            );
                        })}

                        {/* 2. Benchmark Lines (Rendered ON TOP so White Baseline is always visible) */}
                        {chartView === 'cumulative' && (cumulativeSubMode === 'benchmark' || cumulativeSubMode === 'split') && (
                            <>
                                {showWholeBaseline && (
                                    <Line
                                        type="monotone"
                                        dataKey="Whole Tradelist"
                                        stroke="#FFFFFF"
                                        dot={false}
                                        strokeWidth={2.4}
                                        strokeOpacity={1.0}
                                        name="Whole Tradelist (Baseline)"
                                    />
                                )}
                                {showLiveGridFiltered && (
                                    <Line
                                        type="monotone"
                                        dataKey="Live Grid Filtered"
                                        stroke="#10B981"
                                        dot={false}
                                        strokeWidth={2.4}
                                        strokeOpacity={1.0}
                                        name="Live Grid Filtered"
                                    />
                                )}
                            </>
                        )}
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default EquityChartContainer;
